import { EUserRole } from 'enums';
import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';

/**
 * API Urls
 */
const usersUrl = (orgSlug: string) => `/${orgSlug}/${apiVersion}/users`;
const userIdUrl = ({ orgSlug, userId }: { orgSlug: string; userId: number }) =>
  `/${orgSlug}/${apiVersion}/users/${userId}`;
const userRoleUrl = ({ orgSlug, userId }: { orgSlug: string; userId: number }) =>
  `/${orgSlug}/${apiVersion}/user/${userId}/role`;
const userAssignedBuildingsUrl = ({ orgSlug, userId }: { orgSlug: string; userId: number }) =>
  `/${orgSlug}/${apiVersion}/user/${userId}/building/assignment`;
const unassignBuildingsUrl = ({
  orgSlug,
  userId,
  buildingId,
}: {
  orgSlug: string;
  userId: number;
  buildingId: number;
}) => `/${orgSlug}/${apiVersion}/user/${userId}/building/${buildingId}/assignment`;
const userPhoneVerifyUrl = ({ userId }: { userId: number }) => `/${apiVersion}/user/${userId}/phone/verify`;

/**
 * Create APIs
 */
export const usersApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchUsers: build.query<FetchUsersRes, FetchUsersReq>({
      query: ({ orgSlug }) => ({
        url: usersUrl(orgSlug),
      }),
      transformResponse,
      providesTags: ['Users'],
    }),

    updateTeamMember: build.mutation<UpdateTeamMemberRes, UpdateTeamMemberReq>({
      query: ({ orgSlug, userId, user }) => ({
        url: userIdUrl({ orgSlug, userId }),
        method: 'PUT',
        body: user,
      }),
      transformResponse,
      invalidatesTags: ['Users'],
    }),

    updateUserRole: build.mutation<UpdateUserRoleRes, UpdateUserRoleReq>({
      query: ({ orgSlug, userId, role }) => ({
        url: userRoleUrl({ orgSlug, userId }),
        method: 'PUT',
        body: {
          role,
        },
      }),
      transformResponse,
      invalidatesTags: ['Users'],
    }),

    fetchUserAssignedBuildings: build.query<FetchUserAssignedBuildingsRes, FetchUserAssignedBuildingsReq>({
      query: ({ orgSlug, userId }) => ({
        url: userAssignedBuildingsUrl({ orgSlug, userId }),
      }),
      transformResponse,
      providesTags: (result, error, { userId }) => [{ type: 'AssignedBuildings', userId }],
    }),

    assignBuildingsToUser: build.mutation<AssignBuildingsToUserRes, AssignBuildingsToUserReq>({
      query: ({ orgSlug, userId, data }) => ({
        url: userAssignedBuildingsUrl({ orgSlug, userId }),
        method: 'POST',
        body: data,
      }),
      transformResponse,
      invalidatesTags: (result, error, { userId }) => [{ type: 'AssignedBuildings', userId }],
    }),

    unassignBuildingsToUser: build.mutation<UnassignBuildingsToUserRes, UnassignBuildingsToUserReq>({
      query: ({ orgSlug, userId, buildingId }) => ({
        url: unassignBuildingsUrl({ orgSlug, userId, buildingId }),
        method: 'DELETE',
      }),
      transformResponse,
      invalidatesTags: (result, error, { userId }) => [{ type: 'AssignedBuildings', userId }],
    }),

    sendPhoneVerification: build.query<UserPhoneVerifyRes, UserPhoneVerifyReq>({
      query: ({ userId }) => ({
        url: userPhoneVerifyUrl({ userId }),
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchUsersQuery,
  useUpdateTeamMemberMutation,
  useUpdateUserRoleMutation,
  useFetchUserAssignedBuildingsQuery,
  useAssignBuildingsToUserMutation,
  useUnassignBuildingsToUserMutation,
  useSendPhoneVerificationQuery,
} = usersApi;

/**
 * Types
 */
type FetchUsersRes = any;
type FetchUsersReq = {
  orgSlug: string;
};
type UpdateTeamMemberRes = any;
type UpdateTeamMemberReq = {
  orgSlug: string;
  userId: number;
  user: any;
};
type UpdateUserRoleRes = any;
type UpdateUserRoleReq = {
  orgSlug: string;
  userId: number;
  role: EUserRole | null;
};
type FetchUserAssignedBuildingsRes = any;
type FetchUserAssignedBuildingsReq = {
  orgSlug: string;
  userId: number;
};
type AssignBuildingsToUserRes = any;
type AssignBuildingsToUserReq = {
  orgSlug: string;
  userId: number;
  data: {
    buildingIds: number[];
    assignment: 'supervisor' | 'cleaner';
  };
};
type UnassignBuildingsToUserRes = any;
type UnassignBuildingsToUserReq = {
  orgSlug: string;
  userId: number;
  buildingId: number;
};
type UserPhoneVerifyRes = any;
type UserPhoneVerifyReq = {
  userId: number;
};
